import { useState, useCallback, useMemo } from "react";
import { audioMessage as message } from "../Components/Common/Notifications/audioMessage";
import { trainingAPI } from "../api/TrainingAPI";

export const usePlayerNotes = (player, teamId) => {
	const [notes, setNotes] = useState([]);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [newNote, setNewNote] = useState("");
	const [playerNotesLoading, setLoading] = useState(false);
	const [noteIdToEdit, setNoteIdToEdit] = useState(null);

	const showModal = useCallback(() => setIsModalVisible(true), []);

	const handleCancel = useCallback(() => {
		setIsModalVisible(false);
		setNoteIdToEdit(null)
		setNewNote("");
	}, []);

	const handleEditNoteClick = useCallback((noteId, notes) => {
		setIsModalVisible(true);
		setNoteIdToEdit(noteId)
		setNewNote(notes);
	}, []);

	const handleSubmit = useCallback(async () => {
		if (!newNote.trim()) {
			message.warning("Please enter a note");
			return;
		}

		setLoading(true);
		try {
			const response = await trainingAPI.addPlayerNote(
				player,
				teamId,
				newNote
			);
			if (response) {
				setIsModalVisible(false);
				setNewNote("");
				setNoteIdToEdit(null)
				setNotes((prevNotes) => [...prevNotes, response]);
				message.success("Note added successfully");
			}
		} catch (error) {
			message.error("Failed to add note");
		} finally {
			setLoading(false);
		}
	}, [player, teamId, newNote]);

	const handleSubmitEdit = useCallback(async () => {
		if (!newNote.trim()) {
			message.warning("Please enter a note");
			return;
		}

		setLoading(true);
		try {
			const response = await trainingAPI.editPlayerNote(
				newNote,
				noteIdToEdit
			);
			if (response) {
				setIsModalVisible(false);
				setNoteIdToEdit(null);
				setNewNote("");
				setNotes(response);
				message.success("Note edited successfully");
			}
		} catch (error) {
			const error_message = error?.response?.data || "Failed to edit note"
			message.error(error_message);
		} finally {
			setLoading(false);
		}
	}, [player, newNote]);

	const handleDeleteClick = useCallback(async (noteId) => {
		setLoading(true);
		try {
			const response = await trainingAPI.deletePlayerNote(
				noteId
			);
			if (response) {
				setIsModalVisible(false);
				setNoteIdToEdit(null);
				setNewNote("");
				setNotes(response);
				message.success("Note deleted successfully");
			}
		} catch (error) {
			const error_message = error?.response?.data || "Failed to delete note"
			message.error(error_message);
		} finally {
			setLoading(false);
		}
	}, [player, teamId, newNote]);

	const updateNotes = useCallback((newNotes) => {
		setNotes(newNotes);
	}, []);

	const sortedNotes = useMemo(() => {
		return [...notes].sort(
			(a, b) => new Date(b.timestamp) - new Date(a.timestamp)
		);
	}, [notes]);

	return {
		notes: sortedNotes,
		isModalVisible,
		newNote,
		playerNotesLoading,
		showModal,
		handleCancel,
		handleSubmit,
		setNewNote,
		updateNotes,
		noteIdToEdit,
		handleDeleteClick,
		handleEditNoteClick,
		handleSubmitEdit
	};
};
